import styled from "@emotion/styled";
import {
  Button,
  FadeIn,
  FlexRow,
  H2,
  IconChevron,
  Link,
  Stack,
  Text,
} from "atoms";
import { formatDate } from "utils/date";
import { useToggle } from "utils/hooks";
import { formatCentsToDollars } from "utils/formatCentsToDollars";
import { useArtist } from "features/reviewable";
import { paths } from "utils/paths";
import { OrderTag } from "./OrderTag";
import type { ShopOrder } from "../types";
import { useAdminApproveOrder } from "../api/useAdminApproveOrder";

type ShopOrderItemProps = {
  order: ShopOrder;
  showExpandDetails?: boolean;
};
function ShopOrderItem({ order, showExpandDetails }: ShopOrderItemProps) {
  const { mutate: approveOrder, isLoading } = useAdminApproveOrder();
  const [isOpen, toggleIsOpen] = useToggle(false);
  const artist = useArtist(order.artistId);

  const { fulfillmentOrderId, fulfillmentLineItemId } = order;

  return (
    <OrderRow as="li" id={`${order.productId}-${fulfillmentLineItemId}`}>
      <Stack gap="xxs">
        <FlexRow flexWrap="nowrap" alignItems="center" itemsFlex="1 1 auto">
          <OrderItemGrid>
            <FlexRow gap=".66rem" style={{ flex: "1 1 100%" }}>
              <OrderTag status={order.status} />
              <FlexRow gap="8px">
                <small>{formatDate(order.createdAt)}</small> <small>|</small>
                <small>Order {order.orderNumber}</small>
                {showExpandDetails && (
                  <>
                    <small>|</small>
                    <small>
                      <Link to={paths.artistProfile(order.artistId)}>
                        {artist.data?.name}
                      </Link>
                    </small>
                  </>
                )}
              </FlexRow>
            </FlexRow>

            <FlexRow alignItems="flex-start">
              <FlexRow justifyContent="space-between" alignItems="baseline">
                <Stack gap="xs">
                  <FlexRow alignItems="baseline" gap="10px 1.5rem">
                    <H2 size="md" textTransform="none" style={{ margin: 0 }}>
                      {order.productTitle}
                    </H2>

                    {!!order.variantOptions.length && (
                      <Text size="xs" style={{ margin: 0 }}>
                        {order.variantOptions.map((o, index) => {
                          if (index === 0) {
                            return o.value;
                          }
                          return `, ${o.value}`;
                        })}
                      </Text>
                    )}
                  </FlexRow>

                  {showExpandDetails && (
                    <Button size="xxs" variant="text" onClick={toggleIsOpen}>
                      <FlexRow gap="4px">
                        <IconChevron rotate={isOpen ? 0 : 180} />
                        <span>Details</span>
                      </FlexRow>
                    </Button>
                  )}
                </Stack>
              </FlexRow>
            </FlexRow>
          </OrderItemGrid>

          <FlexRow justifyContent="flex-end" style={{ flex: 1 }}>
            {order.discountedPriceInCents && (
              <Stack gap="0">
                <Text size="xxxs" textAlign="right">
                  Price
                </Text>
                <Text size="sm" textAlign="right" bold font="display">
                  {formatCentsToDollars(order.discountedPriceInCents)}
                </Text>
              </Stack>
            )}
          </FlexRow>
        </FlexRow>
        <FadeIn
          hidden={!isOpen}
          style={{
            padding: 10,
            backgroundColor: "#efefef",
            borderRadius: 4,
            marginTop: 10,
          }}
        >
          <small>
            {fulfillmentOrderId && order.status === "UNDER-REVIEW" && (
              <Button
                onClick={() => approveOrder({ fulfillmentOrderId })}
                disabled={isLoading}
              >
                Approve Order
              </Button>
            )}
            <pre>{JSON.stringify(order, null, 2)}</pre>
          </small>
        </FadeIn>
      </Stack>
    </OrderRow>
  );
}

const OrderRow = styled.div(({ theme }) => ({
  marginBottom: 20,
  paddingBottom: 20,
  borderBottom: "1px solid",
  borderBottomColor: theme.colors.fg20,
}));

const OrderItemGrid = styled.div({
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  gap: "10px 1rem",
});

export { ShopOrderItem, OrderItemGrid };
